import { LinkContext } from '@anm/contexts/LinkContext';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import capitalize from '@anm/helpers/string/capitalize';
import { useContext, FC } from 'react';

import NumberedList from './NumberedList';
import { Icon, ListItem, ListWrapper, ShowMoreButton, Wrapper } from './Wrapper';
import getItemLink from './helpers/getItemLink';
import { useLoadMore } from './hooks/useLoadMore';
import { PaginationProps, PrevNextItemProps, PrevNextLinkWrapperProps } from './types';

const FIRST_PAGE_INDEX = 1;
const MIN_PAGES_COUNT = 2;
const DEFAULT_PAGE_TO_SHOW_COUNT = 3;

const PrevNextLinkWrapper: FC<PrevNextLinkWrapperProps> = ({ link, isDisabled, children }) => {
  const { Link } = useContext(LinkContext);

  return isDisabled ? <span>{children}</span> : <Link {...link}> {children}</Link>;
};

const PrevNextItem: FC<PrevNextItemProps> = ({ link, target, isDisabled }) => (
  <ListItem {...{ isDisabled }} key={target} isNext={target === 'next'} isPrev={target === 'previous'}>
    <PrevNextLinkWrapper {...{ link, isDisabled }}>
      {isMobileOrIPad() ? (
        <Icon src="images/icons/pagination_arrow.svg" isNext={target === 'next'} />
      ) : (
        <>{capitalize(target)}</>
      )}
    </PrevNextLinkWrapper>
  </ListItem>
);

const Pagination: FC<PaginationProps> = ({
  link,
  pagesCount,
  onLoadMore,
  currentPage = FIRST_PAGE_INDEX,
  maxPagesToShow = DEFAULT_PAGE_TO_SHOW_COUNT
}) => {
  const handleLoadMore = useLoadMore({ currentPage, onLoadMore });

  if (pagesCount < MIN_PAGES_COUNT) return null;

  const isPrevDisabled = currentPage === FIRST_PAGE_INDEX;
  const isNextDisabled = currentPage === pagesCount;
  const cahShowLoadMore = onLoadMore && !isNextDisabled;

  const prevItemLink = getItemLink({
    link,
    page: currentPage,
    target: 'prev'
  });

  const nextItemLink = getItemLink({
    link,
    page: currentPage,
    target: 'next'
  });

  const canShowPrev = !(isMobileOrIPad() && isPrevDisabled);
  const canShowNext = !(isMobileOrIPad() && isNextDisabled);

  return (
    <Wrapper>
      {cahShowLoadMore && (
        <ShowMoreButton variant="gray" size="full-width" onClick={handleLoadMore}>
          Show more
        </ShowMoreButton>
      )}

      <ListWrapper>
        {canShowPrev && <PrevNextItem link={prevItemLink} isDisabled={isPrevDisabled} target="previous" />}
        <NumberedList {...{ link, maxPagesToShow, currentPage, pagesCount }} />
        {canShowNext && <PrevNextItem link={nextItemLink} isDisabled={isNextDisabled} target="next" />}
      </ListWrapper>
    </Wrapper>
  );
};

export default Pagination;
